import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useWishlist } from "@/context/WishlistContext";
import { useCurrency } from "@/context/CurrencyContext";
import { ShoppingCart, Minus, Plus, Star, ArrowLeft, Truck, ShieldCheck, RefreshCw, Heart } from "lucide-react";
import { toast } from "sonner";
import ProductReviews from "@/components/ProductReviews";

export default function ProductDetail() {
    const { slug } = useParams();
    const [product, setProduct] = useState(null);
    const [qty, setQty] = useState(1);
    const [loading, setLoading] = useState(true);
    const { add } = useCart();
    const { has, toggle } = useWishlist();
    const { format } = useCurrency();

    function load() {
        setLoading(true);
        api.get(`/products/${slug}`)
            .then((r) => setProduct(r.data))
            .catch(() => setProduct(null))
            .finally(() => setLoading(false));
    }
    useEffect(load, [slug]);

    if (loading) return <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-24">Loading…</div>;
    if (!product) return (
        <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-24 text-center">
            <div className="font-display text-3xl mb-4">Product not found</div>
            <Link to="/products" className="text-primary font-semibold">← Back to catalogue</Link>
        </div>
    );

    const wished = has(product.id);

    return (
        <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-12" data-testid="product-detail-page">
            <Link to="/products" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-primary mb-6">
                <ArrowLeft className="w-4 h-4" /> Back to all products
            </Link>
            <div className="grid md:grid-cols-2 gap-12">
                <div className="bg-surface aspect-square overflow-hidden">
                    <img src={product.image_url} alt={product.name} className="w-full h-full object-cover" />
                </div>
                <div>
                    <div className="overline text-primary mb-3">{product.category}</div>
                    <h1 className="font-display text-3xl md:text-5xl font-bold leading-tight mb-4" data-testid="product-title">{product.name}</h1>
                    <div className="flex items-center gap-3 mb-6">
                        <div className="flex gap-0.5">
                            {Array.from({ length: 5 }).map((_, i) => (
                                <Star key={i} className={`w-4 h-4 ${i < Math.round(product.rating) ? "fill-accent text-accent" : "text-muted-foreground"}`} />
                            ))}
                        </div>
                        <span className="text-sm text-muted-foreground">{product.rating} rating</span>
                    </div>
                    <div className="font-display font-bold text-4xl mb-6" data-testid="product-price">{format(product.price)}</div>
                    <p className="text-muted-foreground leading-relaxed mb-8">{product.description}</p>

                    <div className="flex items-center gap-3 mb-6">
                        <div className="flex items-center border border-border">
                            <button onClick={() => setQty(Math.max(1, qty - 1))} className="p-3 hover:bg-muted" data-testid="qty-decrement"><Minus className="w-4 h-4" /></button>
                            <span className="px-5 font-semibold tabular-nums" data-testid="qty-value">{qty}</span>
                            <button onClick={() => setQty(qty + 1)} className="p-3 hover:bg-muted" data-testid="qty-increment"><Plus className="w-4 h-4" /></button>
                        </div>
                        <button
                            onClick={() => { add(product, qty); toast.success(`${qty} × ${product.name} added`); }}
                            className="flex-1 inline-flex items-center justify-center gap-2 bg-primary text-white px-6 py-3 font-semibold hover:bg-primary-hover transition-colors"
                            data-testid="product-add-to-cart-btn"
                        >
                            <ShoppingCart className="w-4 h-4" /> Add to cart
                        </button>
                        <button
                            onClick={() => { toggle(product); toast.success(wished ? "Removed from wishlist" : "Saved to wishlist"); }}
                            className={`p-3 border ${wished ? "bg-primary text-white border-primary" : "border-border hover:border-primary"} transition-colors`}
                            data-testid="product-wishlist-btn"
                            aria-label="Toggle wishlist"
                        >
                            <Heart className="w-4 h-4" fill={wished ? "white" : "none"} />
                        </button>
                    </div>

                    <div className="grid grid-cols-3 gap-4 pt-8 border-t border-border text-xs">
                        <div className="flex flex-col items-center text-center gap-2">
                            <Truck className="w-5 h-5 text-primary" />
                            <span>Nationwide delivery</span>
                        </div>
                        <div className="flex flex-col items-center text-center gap-2">
                            <ShieldCheck className="w-5 h-5 text-primary" />
                            <span>Secure payment</span>
                        </div>
                        <div className="flex flex-col items-center text-center gap-2">
                            <RefreshCw className="w-5 h-5 text-primary" />
                            <span>7-day returns</span>
                        </div>
                    </div>
                </div>
            </div>

            <ProductReviews slug={slug} onRatingChange={load} />
        </div>
    );
}
